import { getApifyRunDetails } from '@/services/api/apify';
import { dataCollectionAPI } from '@/services/api/dataCollection';

/**
 * Utility function to sync a specific Apify run by ID
 * Useful for debugging or manual syncing of stuck runs
 *
 * Example usage:
 * ```
 * import { syncApifyRun } from '@/utils/syncApifyRun';
 *
 * // In browser console:
 * await syncApifyRun('xWWlYhZ98dY8y0r96');
 * ```
 */
export async function syncApifyRun(runId: string): Promise<boolean> {
  try {
    console.log(`Fetching Apify status for run: ${runId}`);

    // Get current status from Apify
    const apifyRun = await getApifyRunDetails(runId);

    if (!apifyRun) {
      console.error(`Run ${runId} not found in Apify`);
      return false;
    }

    console.log(`Apify run status: ${apifyRun.status}`);
    console.log(`Started at: ${apifyRun.startedAt}`);
    console.log(`Finished at: ${apifyRun.finishedAt || 'Still running'}`);

    // Map Apify status
    const apifyStatus = apifyRun.status.toLowerCase();
    let mappedStatus = 'running';

    if (apifyStatus === 'succeeded' || apifyStatus === 'finished') {
      mappedStatus = 'completed';
    } else if (
      apifyStatus === 'failed' ||
      apifyStatus === 'aborted' ||
      apifyStatus === 'timed-out'
    ) {
      mappedStatus = 'failed';
    }

    console.log(`Mapped status: ${mappedStatus}`);

    // Sync to backend
    const response = await dataCollectionAPI.syncRunStatus(runId, {
      sync_from_apify: true,
      status: mappedStatus,
      completed_at: apifyRun.finishedAt || undefined
    });

    console.log('Sync response:', response);
    console.log(
      `✅ Successfully synced run ${runId} - Status updated from ${response.old_status} to ${response.new_status}`
    );

    // If the run completed successfully, trigger backfill to process the dataset
    if (mappedStatus === 'completed') {
      console.log('🔄 Triggering dataset processing...');
      try {
        const backfillResult = await dataCollectionAPI.backfillRun(runId, true);
        console.log('Backfill result:', backfillResult);
        console.log(
          `✅ Dataset processed successfully - ${backfillResult.result.records || 0} records`
        );

        if (backfillResult.result.inventory_summary) {
          console.log('Inventory Summary:', backfillResult.result.inventory_summary);
        }
        if (backfillResult.result.validation_status) {
          console.log('Validation Status:', backfillResult.result.validation_status);
        }
      } catch (backfillError) {
        console.error('❌ Dataset processing failed:', backfillError);
        console.log(
          'Note: Status was updated but dataset processing failed. You may need to manually process the dataset.'
        );
      }
    }

    return true;
  } catch (error) {
    console.error(`Failed to sync run ${runId}:`, error);
    return false;
  }
}

// Export to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).syncApifyRun = syncApifyRun;
}
